/**
 * InstallAppCard — surface permanente d'installation dans /settings.
 *
 * Contrairement au bandeau (InstallAppNudge.tsx), aucune logique
 * anti-harcèlement ici : c'est l'endroit qu'on retrouve exprès, pas une
 * relance. Rendue `null` si l'app tourne déjà en PWA installée
 * (`isStandalonePwa()`).
 *
 * Placée entre la section « Crédits » et `<PairingPanel />` dans
 * Settings.tsx — l'ordre compte : sur iOS, installer puis appairer est une
 * seule procédure pour l'utilisateur (voir la note ci-dessous), pas deux
 * sujets séparés.
 *
 * Trois cas :
 *  - `beforeinstallprompt` capté (Android / desktop Chromium) : bouton qui
 *    déclenche l'invite native.
 *  - iOS (Safari n'émet jamais cet événement) : les gestes manuels
 *    (Partager → « Sur l'écran d'accueil ») ET la suite indispensable —
 *    l'app installée ne partage pas le stockage de Safari et ne peut donc
 *    pas recevoir le lien de connexion par e-mail ; il faut y entrer avec
 *    un code, que `PairingPanel` juste en dessous permet de générer sur cet
 *    appareil-ci (celui qui, lui, a encore la session Safari active).
 *  - Android sans événement (navigateur qui ne le supporte pas encore) :
 *    une ligne d'instruction manuelle.
 *  - Desktop sans événement (Firefox, Safari macOS) : rien — pas
 *    d'instruction honnête à donner, même arbitrage que memoneo.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download } from "lucide-react";
import { Card, Button, MutedText } from "./ui";
import { isStandalonePwa } from "../lib/platform";
import { isNative } from "../native/platform";
import { detectInstallPlatform } from "../lib/installNudge";
import { useDeferredInstallPrompt, triggerInstallPrompt } from "../lib/installPrompt";

export function InstallAppCard() {
  const { t } = useTranslation();
  const deferredEvent = useDeferredInstallPrompt();
  const [installing, setInstalling] = useState(false);

  const platform = useMemo(
    () => detectInstallPlatform(navigator.userAgent, navigator.maxTouchPoints),
    [],
  );

  // In a store app there is nothing to install: it is already the app.
  if (isStandalonePwa() || isNative()) return null;

  // Rien d'honnête à montrer sur un desktop qui n'a pas émis l'événement.
  const hasContent = !!deferredEvent || platform === "ios" || platform === "android";
  if (!hasContent) return null;

  async function handleInstall(): Promise<void> {
    setInstalling(true);
    try {
      await triggerInstallPrompt();
    } finally {
      setInstalling(false);
    }
  }

  return (
    <section>
      <h2 className="font-heading font-bold text-lg mb-3">{t("install.title")}</h2>
      <Card className="flex flex-col gap-3">
        <MutedText>{t("install.pitch")}</MutedText>

        {deferredEvent ? (
          <Button onClick={() => void handleInstall()} disabled={installing} className="w-full">
            <Download size={16} />
            {t("install.cta")}
          </Button>
        ) : platform === "ios" ? (
          <>
            <MutedText>{t("install.iosSteps")}</MutedText>
            <MutedText>{t("install.iosPairingNote")}</MutedText>
          </>
        ) : (
          <MutedText>{t("install.androidSteps")}</MutedText>
        )}
      </Card>
    </section>
  );
}
